import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import ErrorMessage from '@minecraft.error-message';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import { useTranslation } from '@blocs.i18n';
import {
  FormItem,
  useFiltersLabel,
  generateSelectOptionsBasedOnField,
  FiltersNameEnum,
  FilterDrawerType,
} from '@blocs.filter';
import { FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { FilterTitle } from '../../shared/styles';
import { VehicleType, VehicleYearRange, VehicleMake, VehicleModel } from '../../FilterDrawerCollection';
import { QUERY_VEHICLE } from './VehicleCategoryGraphQL';

interface Props {
  type: FilterDrawerType;
}

const VehicleCategory: FC<Props> = ({ type }) => {
  const { t } = useTranslation();
  const filtersLabelOfAudience = useFiltersLabel(type);

  const { data, error, loading } = useQuery(QUERY_VEHICLE);

  const { isCategoryMatchedSearch, shouldCategoryFilterBeVisible, assignOptionsToCategoryFilters } =
    useDrawerSearchFilterNameOrCategory(FiltersCategoryNameEnum.vehicle);
  const { filtersCounts } = useDrawerFiltersCounts();

  const { vehicleTypeOptions, vehicleMakeOptions } = useMemo(() => {
    const { vehicleMakes = [], vehicleTypes = [] } = data || {};

    return {
      vehicleTypeOptions: generateSelectOptionsBasedOnField(vehicleTypes, 'name'),
      vehicleMakeOptions: generateSelectOptionsBasedOnField(
        vehicleMakes,
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.vehicleMakeId]
      ),
    };
  }, [data, filtersCounts]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (vehicleTypeOptions.length) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.vehicleType]: vehicleTypeOptions,
        [FiltersNameEnum.vehicleMake]: vehicleMakeOptions,
      });
    }
  }, [vehicleTypeOptions]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <>
        <FilterTitle>{t('common:profile.assets.vehicle')}</FilterTitle>
        {shouldCategoryFilterBeVisible(FiltersNameEnum.vehicleType) && (
          <FormItem $fullWidth $marginValue={2}>
            <VehicleType
              name={FiltersNameEnum.vehicleType}
              label={filtersLabelOfAudience[FiltersNameEnum.vehicleType]}
              options={vehicleTypeOptions}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.vehicleYearRange) && (
          <FormItem $fullWidth $marginValue={2}>
            <VehicleYearRange
              rangeName={FiltersNameEnum.vehicleYearRange}
              minName={FiltersNameEnum.min}
              maxName={FiltersNameEnum.max}
              label={filtersLabelOfAudience[FiltersNameEnum.vehicleYearRange]}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.vehicleMake) && (
          <FormItem $fullWidth $marginValue={2}>
            <VehicleMake
              name={FiltersNameEnum.vehicleMake}
              label={filtersLabelOfAudience[FiltersNameEnum.vehicleMake]}
              options={vehicleMakeOptions}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.vehicleModel) && (
          <FormItem $fullWidth $marginValue={2}>
            <VehicleModel
              name={FiltersNameEnum.vehicleModel}
              label={filtersLabelOfAudience[FiltersNameEnum.vehicleModel]}
            />
          </FormItem>
        )}
      </>
    )
  );
};

export default VehicleCategory;
